import type { BadgeVariant } from '../../components/ui/Badge';
import type { KhoaDich } from '../../i18n';
import { MA_QUYEN_XEM_PII } from './nghiep-vu/ekyc';
import type {
  CheDoLiveness,
  DongTac,
  KetLuanHangMuc,
  KhiThieu,
  LyDoTat,
  MaHangMuc,
  MucNopLai,
  MucRuiRo,
  MucVanDe,
  NguonOcr,
  NhomBuoc,
  QuyetDinh,
  TrangThaiGiao,
  TrangThaiPhien,
} from './types';

/**
 * Bảng tông của module eKYC. Nằm ở đây chứ không ở `src/mock/`: tầng mock sẽ bị xoá khi API thật
 * về, còn ý nghĩa màu là của phân hệ (CLAUDE.md).
 *
 * Chỉ dùng nhóm biến thể **trạng thái** của `Badge` — `success`/`warning`/`danger`/`primary`/
 * `neutral`. Nhóm phân loại (`violet`/`teal`/`orange`/`info`) cấm dùng lẫn vào đây.
 *
 * Nguyên tắc tô màu của riêng phân hệ này, suy từ `04` §13 và `05` §8:
 *
 * - **Hổ phách = đang chờ một con người.** Đó là nơi hồ sơ đứng lại nếu không ai đụng vào.
 * - **Đỏ = một lời khẳng định về một con người** ("đã kiểm, và không khớp"), hoặc một lỗi mà người
 *   vận hành phải xử lý.
 * - **Xám = không có tín hiệu**, không phải tín hiệu xấu. Đây là chỗ hay bị tô sai nhất: `SKIPPED`
 *   và `ERROR` đều mang `score = 0`, và tô chúng màu đỏ là biến "chưa kiểm" thành "đã trượt".
 */
type BienTheTrangThai = Extract<BadgeVariant, 'neutral' | 'primary' | 'success' | 'warning' | 'danger'>;

/**
 * Vòng đời phiên định danh (`03` §4).
 *
 * Chín chặng giữa luồng xanh nhấn: máy đang chạy, không ai cần làm gì. `MANUAL_REVIEW` và
 * `ADDITIONAL_REQUIRED` hổ phách vì **đang chờ một người**. `VERIFIED` xanh, `REJECTED` đỏ.
 *
 * `EXPIRED`/`CANCELLED` xám chứ **không** đỏ: khách bỏ dở không phải một sự cố, và tô đỏ làm người
 * vận hành đi tìm lỗi ở chỗ không có lỗi. `ERROR` thì đỏ — đó là máy chủ hỏng, có người phải xem.
 */
export const TRANG_THAI_PHIEN: Record<TrangThaiPhien, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  CREATED: { khoa: 'ekyc.trangThai.CREATED', variant: 'primary' },
  CONSENT_REQUIRED: { khoa: 'ekyc.trangThai.CONSENT_REQUIRED', variant: 'primary' },
  DOCUMENT_REQUIRED: { khoa: 'ekyc.trangThai.DOCUMENT_REQUIRED', variant: 'primary' },
  OCR_PROCESSING: { khoa: 'ekyc.trangThai.OCR_PROCESSING', variant: 'primary' },
  FRAUD_CHECKING: { khoa: 'ekyc.trangThai.FRAUD_CHECKING', variant: 'primary' },
  INFO_CONFIRMATION: { khoa: 'ekyc.trangThai.INFO_CONFIRMATION', variant: 'primary' },
  SELFIE_REQUIRED: { khoa: 'ekyc.trangThai.SELFIE_REQUIRED', variant: 'primary' },
  LIVENESS_PROCESSING: { khoa: 'ekyc.trangThai.LIVENESS_PROCESSING', variant: 'primary' },
  VERIFICATION_PROCESSING: { khoa: 'ekyc.trangThai.VERIFICATION_PROCESSING', variant: 'primary' },
  VERIFIED: { khoa: 'ekyc.trangThai.VERIFIED', variant: 'success' },
  REJECTED: { khoa: 'ekyc.trangThai.REJECTED', variant: 'danger' },
  MANUAL_REVIEW: { khoa: 'ekyc.trangThai.MANUAL_REVIEW', variant: 'warning' },
  ADDITIONAL_REQUIRED: { khoa: 'ekyc.trangThai.ADDITIONAL_REQUIRED', variant: 'warning' },
  EXPIRED: { khoa: 'ekyc.trangThai.EXPIRED', variant: 'neutral' },
  CANCELLED: { khoa: 'ekyc.trangThai.CANCELLED', variant: 'neutral' },
  ERROR: { khoa: 'ekyc.trangThai.ERROR', variant: 'danger' },
  DELETED: { khoa: 'ekyc.trangThai.DELETED', variant: 'neutral' },
};

/** Quyết định của động cơ rủi ro (`05` §6). `REVIEW` hổ phách: đang chờ một người. */
export const QUYET_DINH: Record<QuyetDinh, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  PASS: { khoa: 'ekyc.quyetDinh.PASS', variant: 'success' },
  REVIEW: { khoa: 'ekyc.quyetDinh.REVIEW', variant: 'warning' },
  REJECT: { khoa: 'ekyc.quyetDinh.REJECT', variant: 'danger' },
};

/** Phân vùng rủi ro (`05` §6). Cao là xấu — ngược chiều với điểm của hạng mục. */
export const MUC_RUI_RO: Record<MucRuiRo, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  LOW: { khoa: 'ekyc.ruiRo.LOW', variant: 'success' },
  MEDIUM: { khoa: 'ekyc.ruiRo.MEDIUM', variant: 'warning' },
  HIGH: { khoa: 'ekyc.ruiRo.HIGH', variant: 'danger' },
};

/**
 * Kết luận một hạng mục (`04` §1.2) — bảng tông quan trọng nhất của phân hệ.
 *
 * `SKIPPED` và `ERROR` **đều xám**, dù `ERROR` là một sự cố: cả hai nói "**chưa có câu trả lời**",
 * và đó là thứ người duyệt cần đọc ra ngay. Tô `ERROR` màu đỏ cạnh một `FAIL` cũng màu đỏ làm hai
 * thứ trông như nhau — trong khi `FAIL` là "người này không khớp" còn `ERROR` là "máy chủ hỏng".
 * Sự cố của `ERROR` được nói bằng chữ (và bằng mã lỗi), không bằng màu.
 */
export const KET_LUAN_HANG_MUC: Record<KetLuanHangMuc, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  PASS: { khoa: 'ekyc.ketLuan.PASS', variant: 'success' },
  REVIEW: { khoa: 'ekyc.ketLuan.REVIEW', variant: 'warning' },
  FAIL: { khoa: 'ekyc.ketLuan.FAIL', variant: 'danger' },
  SKIPPED: { khoa: 'ekyc.ketLuan.SKIPPED', variant: 'neutral' },
  ERROR: { khoa: 'ekyc.ketLuan.ERROR', variant: 'neutral' },
};

/** Mức nghiêm trọng của một vấn đề (`04` §1.2). */
export const MUC_VAN_DE: Record<MucVanDe, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  LOW: { khoa: 'ekyc.mucVanDe.LOW', variant: 'neutral' },
  MEDIUM: { khoa: 'ekyc.mucVanDe.MEDIUM', variant: 'warning' },
  HIGH: { khoa: 'ekyc.mucVanDe.HIGH', variant: 'danger' },
};

/**
 * Năm hạng mục, theo **thứ tự chạy thật** (`01` §4.1) chứ không theo bảng chữ cái.
 *
 * Thứ tự rẻ-trước-đắt-sau là thứ tự người duyệt đọc kết quả: ảnh mờ tịt trượt ngay ở `sanity` và
 * mọi hạng mục sau đó không còn ý nghĩa. Sắp theo chữ cái đặt `faceMatch` lên đầu và làm mất mạch
 * nhân quả ấy.
 */
export const THU_TU_HANG_MUC: MaHangMuc[] = ['sanity', 'logic', 'fraud', 'liveness', 'faceMatch'];

export const TEN_HANG_MUC: Record<MaHangMuc, KhoaDich> = {
  sanity: 'ekyc.tenHangMuc.sanity',
  logic: 'ekyc.tenHangMuc.logic',
  fraud: 'ekyc.tenHangMuc.fraud',
  liveness: 'ekyc.tenHangMuc.liveness',
  faceMatch: 'ekyc.tenHangMuc.faceMatch',
};

/** Câu một dòng nói hạng mục đó đo gì — người duyệt không thuộc năm cái tên này. */
export const MO_HANG_MUC: Record<MaHangMuc, KhoaDich> = {
  sanity: 'ekyc.hangMucMo.sanity',
  logic: 'ekyc.hangMucMo.logic',
  fraud: 'ekyc.hangMucMo.fraud',
  liveness: 'ekyc.hangMucMo.liveness',
  faceMatch: 'ekyc.hangMucMo.faceMatch',
};

/**
 * Chín yếu tố rủi ro theo thứ tự trọng số giảm dần (`05` §2.1).
 *
 * Ba yếu tố cuối (`pad`, `device`, `authority`) **chưa có phân hệ** ở v1 nên luôn `null`. Chúng
 * vẫn hiện trên màn, với chữ "không có tín hiệu" — giấu đi thì người duyệt tưởng hệ thống đã kiểm
 * đủ chín thứ (`08` §6: "một bước an ninh đang bật mà không chạy là điều tệ hơn hẳn một bước tắt").
 */
export const THU_TU_YEU_TO = [
  'fraud',
  'liveness',
  'pad',
  'face',
  'document',
  'ocr',
  'device',
  'authority',
  'behavior',
] as const;

export const TEN_YEU_TO: Record<(typeof THU_TU_YEU_TO)[number], KhoaDich> = {
  fraud: 'ekyc.yeuTo.fraud',
  liveness: 'ekyc.yeuTo.liveness',
  pad: 'ekyc.yeuTo.pad',
  face: 'ekyc.yeuTo.face',
  document: 'ekyc.yeuTo.document',
  ocr: 'ekyc.yeuTo.ocr',
  device: 'ekyc.yeuTo.device',
  authority: 'ekyc.yeuTo.authority',
  behavior: 'ekyc.yeuTo.behavior',
};

/** Sáu loại giấy tờ (`04` §12.1). Hiện bằng chữ, không tô màu: loại không mang nghĩa nặng/nhẹ. */
export const LOAI_GIAY_TO: Record<string, KhoaDich> = {
  VIETNAM_CCCD: 'ekyc.loaiGiayTo.VIETNAM_CCCD',
  VIETNAM_CCCD_BARCODE: 'ekyc.loaiGiayTo.VIETNAM_CCCD_BARCODE',
  VIETNAM_CMND: 'ekyc.loaiGiayTo.VIETNAM_CMND',
  VIETNAM_PASSPORT: 'ekyc.loaiGiayTo.VIETNAM_PASSPORT',
  VIETNAM_DRIVER_LICENSE: 'ekyc.loaiGiayTo.VIETNAM_DRIVER_LICENSE',
  LAOS_ID: 'ekyc.loaiGiayTo.LAOS_ID',
};

/** Vai trò ảnh mở được ở màn thẩm định. Ảnh stream qua API, không bao giờ qua presigned URL. */
export const VAI_TRO_ANH = ['DOCUMENT_FRONT', 'DOCUMENT_BACK', 'SELFIE'] as const;

/** Mục người thẩm định yêu cầu khách nộp lại, theo thứ tự trên màn — khớp `MUC_NOP_LAI` của máy chủ. */
export const MUC_NOP_LAI: readonly MucNopLai[] = ['DOCUMENT_FRONT', 'DOCUMENT_BACK', 'SELFIE', 'SELFIE_VIDEO'];

export const TEN_MUC_NOP_LAI: Record<MucNopLai, KhoaDich> = {
  DOCUMENT_FRONT: 'ekyc.mucNopLai.DOCUMENT_FRONT',
  DOCUMENT_BACK: 'ekyc.mucNopLai.DOCUMENT_BACK',
  SELFIE: 'ekyc.mucNopLai.SELFIE',
  SELFIE_VIDEO: 'ekyc.mucNopLai.SELFIE_VIDEO',
};

export const TEN_VAI_TRO_ANH: Record<(typeof VAI_TRO_ANH)[number], KhoaDich> = {
  DOCUMENT_FRONT: 'ekyc.anh.DOCUMENT_FRONT',
  DOCUMENT_BACK: 'ekyc.anh.DOCUMENT_BACK',
  SELFIE: 'ekyc.anh.SELFIE',
};

/**
 * Tab của màn Hồ sơ định danh. Mặc định mở `cho-nguoi` — đó là việc người vận hành làm được ngay,
 * còn `tat-ca` là một danh sách không có hành động nào gắn với nó.
 */
export const TAB_HO_SO = ['tat-ca', 'cho-nguoi', 'dang-chay', 'dat', 'tu-choi', 'roi'] as const;

export const TEN_TAB_HO_SO: Record<(typeof TAB_HO_SO)[number], KhoaDich> = {
  'tat-ca': 'ekyc.tab.tatCa',
  'cho-nguoi': 'ekyc.tab.choNguoi',
  'dang-chay': 'ekyc.tab.dangChay',
  dat: 'ekyc.tab.dat',
  'tu-choi': 'ekyc.tab.tuChoi',
  roi: 'ekyc.tab.roi',
};

/**
 * Mã quyền module. Sáu module quyền của eKYC (`06` §2.2, §2.3) — `EKYC_REVIEW` là **module riêng**,
 * không phải một action của `EKYC_SESSION`: bản đầu đòi `EKYC_SESSION.REVIEW`, mà `REVIEW` không
 * nằm trong bộ action được seed, nên quyền ấy **không bao giờ cấp được cho ai** (`07` §18.2).
 *
 * Thiếu quyền thì **ẩn** mục, không hiện mờ (ADR-UI-0005); server vẫn là chốt cuối.
 */
export const QUYEN = {
  tongQuan: 'EKYC_DASHBOARD.READ',
  phienDoc: 'EKYC_SESSION.READ',
  phienTao: 'EKYC_SESSION.CREATE',
  /** Huỷ hồ sơ đang chạy — cùng quyền `ekyc:ho-so:update` với gửi ảnh, nộp ở máy chủ. */
  phienSua: 'EKYC_SESSION.UPDATE',
  /** Xoá dữ liệu cá nhân của hồ sơ (`ekyc:ho-so:delete`). */
  phienXoa: 'EKYC_SESSION.DELETE',
  /** Mở ảnh mặt người và số giấy tờ thật — tách khỏi `EKYC_SESSION` có chủ ý (`06` §1.8). */
  /*
   * Dùng chung hằng số với `nghiep-vu/ekyc.xemDuocPii` thay vì chép chuỗi: `coMaQuyen` khớp
   * **chính xác**, không có ký tự đại diện, nên hai bản chép lệch một ký tự là vùng ảnh ẩn mất mà
   * test của hàm thuần vẫn xanh. Ở đây `tsc` canh thay.
   */
  thamDinhDoc: MA_QUYEN_XEM_PII,
  thamDinhGhi: 'EKYC_REVIEW.UPDATE',
  quyTrinhDoc: 'EKYC_WORKFLOW.READ',
  quyTrinhTao: 'EKYC_WORKFLOW.CREATE',
  quyTrinhSua: 'EKYC_WORKFLOW.UPDATE',
  quyTrinhXoa: 'EKYC_WORKFLOW.DELETE',
  ungDungDoc: 'EKYC_APP.READ',
  ungDungTao: 'EKYC_APP.CREATE',
  ungDungSua: 'EKYC_APP.UPDATE',
} as const;

/** Số dòng mỗi trang ở màn Hồ sơ định danh. */
export const MOT_TRANG = 20;

/** Nhịp hỏi lại khi còn phiên đang chạy, mili giây. */
export const NHIP_HOI = 3000;

/** Số liệu tổng quan tính trên bao nhiêu ngày gần nhất. */
export const SO_NGAY_THONG_KE = 30;

// ----- Quy trình định danh và ứng dụng ---------------------------------------------------------------------------

/**
 * Nhãn từng bước theo mã của máy chủ. Mã lạ (máy chủ thêm bước mới trước khi giao diện kịp dịch) hiện **chính mã
 * ấy** — không giấu: một bước an ninh không có tên vẫn tốt hơn một bước biến mất khỏi màn hình.
 */
export const TEN_BUOC: Readonly<Record<string, KhoaDich>> = {
  dong_y: 'ekyc.buoc.dong_y',
  mat_truoc: 'ekyc.buoc.mat_truoc',
  mat_sau: 'ekyc.buoc.mat_sau',
  chan_dung: 'ekyc.buoc.chan_dung',
  nfc: 'ekyc.buoc.nfc',
  chat_luong_anh: 'ekyc.buoc.chat_luong_anh',
  boc_tach: 'ekyc.buoc.boc_tach',
  mrz_logic: 'ekyc.buoc.mrz_logic',
  khach_xac_nhan: 'ekyc.buoc.khach_xac_nhan',
  gia_mao: 'ekyc.buoc.gia_mao',
  thuc_the_song: 'ekyc.buoc.thuc_the_song',
  doi_chieu_mat: 'ekyc.buoc.doi_chieu_mat',
  pad: 'ekyc.buoc.pad',
  kiem_trung: 'ekyc.buoc.kiem_trung',
  doi_soat_co_quan: 'ekyc.buoc.doi_soat_co_quan',
  kiem_thiet_bi: 'ekyc.buoc.kiem_thiet_bi',
  cham_diem: 'ekyc.buoc.cham_diem',
  duyet_tay: 'ekyc.buoc.duyet_tay',
};

export const TEN_NHOM_BUOC: Record<NhomBuoc, KhoaDich> = {
  'thu-thap': 'ekyc.nhomBuoc.thuThap',
  'giay-to': 'ekyc.nhomBuoc.giayTo',
  'khuon-mat': 'ekyc.nhomBuoc.khuonMat',
  'doi-soat': 'ekyc.nhomBuoc.doiSoat',
  'quyet-dinh': 'ekyc.nhomBuoc.quyetDinh',
};

export const LY_DO_TAT: Record<LyDoTat, KhoaDich> = {
  chua_kha_dung: 'ekyc.quyTrinh.chuaKhaDung',
  thieu_phu_thuoc: 'ekyc.quyTrinh.thieuPhuThuoc',
  nguoi_dung_tat: 'ekyc.quyTrinh.nguoiDungTat',
};

export const TEN_CHE_DO_LIVENESS: Record<CheDoLiveness, KhoaDich> = {
  PASSIVE: 'ekyc.cheDoLiveness.PASSIVE',
  ACTIVE: 'ekyc.cheDoLiveness.ACTIVE',
  HYBRID: 'ekyc.cheDoLiveness.HYBRID',
};

export const TEN_DONG_TAC: Record<DongTac, KhoaDich> = {
  TURN_LEFT: 'ekyc.dongTac.TURN_LEFT',
  TURN_RIGHT: 'ekyc.dongTac.TURN_RIGHT',
  LOOK_UP: 'ekyc.dongTac.LOOK_UP',
  LOOK_DOWN: 'ekyc.dongTac.LOOK_DOWN',
  MOVE_CLOSER: 'ekyc.dongTac.MOVE_CLOSER',
};

export const TEN_NGUON_OCR: Record<NguonOcr, KhoaDich> = {
  '': 'ekyc.nguonOcr.macDinh',
  service: 'ekyc.nguonOcr.service',
  gemini: 'ekyc.nguonOcr.gemini',
  openai: 'ekyc.nguonOcr.openai',
  local: 'ekyc.nguonOcr.local',
};

export const TEN_KHI_THIEU: Record<KhiThieu, KhoaDich> = {
  MANUAL_REVIEW: 'ekyc.khiThieu.MANUAL_REVIEW',
  REJECT: 'ekyc.khiThieu.REJECT',
};

/** Trường giấy tờ trả về đối tác. Tên trường là hợp đồng nên hiện kèm mã. */
export const TEN_TRUONG_GIAY_TO: Readonly<Record<string, KhoaDich>> = {
  idNumber: 'ekyc.truongGiayTo.idNumber',
  oldIdNumber: 'ekyc.truongGiayTo.oldIdNumber',
  fullName: 'ekyc.truongGiayTo.fullName',
  dob: 'ekyc.truongGiayTo.dob',
  gender: 'ekyc.truongGiayTo.gender',
  issueDate: 'ekyc.truongGiayTo.issueDate',
  expiryDate: 'ekyc.truongGiayTo.expiryDate',
  issuePlace: 'ekyc.truongGiayTo.issuePlace',
  residence: 'ekyc.truongGiayTo.residence',
  nationality: 'ekyc.truongGiayTo.nationality',
  mrz: 'ekyc.truongGiayTo.mrz',
};

/**
 * Lượt giao webhook. `PENDING` xanh nhấn: **máy** đang thử lại, không ai cần làm gì — hổ phách của phân hệ này dành
 * cho "đang chờ một người". `FAILED` đỏ: hết lượt thử, có người phải xem đích của đối tác rồi bấm gửi lại.
 */
export const TRANG_THAI_GIAO: Record<TrangThaiGiao, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  PENDING: { khoa: 'ekyc.trangThaiGiao.PENDING', variant: 'primary' },
  SENT: { khoa: 'ekyc.trangThaiGiao.SENT', variant: 'success' },
  FAILED: { khoa: 'ekyc.trangThaiGiao.FAILED', variant: 'danger' },
};

/** Tiền tố khi tên mở đầu bằng số — mã phải mở đầu bằng chữ (`MAU_MA` của máy chủ). */
export const TIEN_TO_MA_QUY_TRINH = 'QT';
export const TIEN_TO_MA_UNG_DUNG = 'APP';
/** Độ dài tối đa của mã, khớp `ck_ekyc_*_ma` (`^[A-Z][A-Z0-9_]{1,39}$`). */
export const MA_TOI_DA = 40;

/** Giãn nhịp xem trước phía server: đợi người dùng ngừng gõ rồi mới hỏi. */
export const NHIP_XEM_TRUOC_MS = 400;

/**
 * Đoạn mã mẫu kiểm chữ ký webhook (`07` §19.1) cho người tích hợp. Mã, không phải chữ giao diện — giữ nguyên ở
 * cả hai ngôn ngữ, chú thích trong mã viết tiếng Anh như mọi mẫu mã tích hợp.
 */
export const MAU_KIEM_CHU_KY = `const ts = req.headers['x-ekyc-timestamp'];
const expected = 'sha256=' + crypto
  .createHmac('sha256', process.env.EKYC_WEBHOOK_SECRET)
  .update(ts + '.' + rawBody)   // raw bytes, before JSON.parse
  .digest('hex');
const ok = crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(req.headers['x-ekyc-signature']));
// Also reject when |now - ts| > 300 s (replay).`;

/** Quyền module mà đối tác cần trên khoá API (ADR-0085 mục 1). Mã quyền là hợp đồng, không dịch. */
export const QUYEN_KHOA_API_DOI_TAC: readonly { ma: string; khoa: KhoaDich }[] = [
  { ma: 'ekyc:ho-so:create', khoa: 'ekyc.ungDung.quyenMoPhien' },
  { ma: 'ekyc:ho-so:update', khoa: 'ekyc.ungDung.quyenGuiAnh' },
  { ma: 'ekyc:ho-so:read', khoa: 'ekyc.ungDung.quyenDocKetQua' },
  { ma: 'ekyc:tham-dinh:read', khoa: 'ekyc.ungDung.quyenDocPii' },
];
